import { useMobile, MOBILE_TABS, type MobileTab } from '../../contexts/MobileContext';
import { ClipboardList, Calendar, MessageCircle, type LucideIcon } from 'lucide-react';

interface TabConfig {
  id: MobileTab;
  label: string;
  icon: LucideIcon;
}

/**
 * MobileTabBar - Bottom navigation bar for mobile devices
 *
 * Shows 3 tabs: Audit, Plan, Chat
 * Fixed at bottom of screen, only visible on mobile/tablet
 */
export function MobileTabBar() {
  const { activeTab, setActiveTab, showTabBar, isTablet } = useMobile();

  if (!showTabBar) {
    return null;
  }

  const tabs: TabConfig[] = [
    {
      id: MOBILE_TABS.AUDIT,
      label: 'Audit',
      icon: ClipboardList,
    },
    {
      id: MOBILE_TABS.PLANNER,
      label: 'Plan',
      icon: Calendar,
    },
    {
      id: MOBILE_TABS.CHAT,
      label: 'Chat',
      icon: MessageCircle,
    },
  ];

  const getTabStyle = (tab: TabConfig): string => {
    const isActive = activeTab === tab.id;

    const isActiveOnTablet =
      isTablet &&
      activeTab !== MOBILE_TABS.AUDIT &&
      (tab.id === MOBILE_TABS.PLANNER || tab.id === MOBILE_TABS.CHAT);

    if (isActive || isActiveOnTablet) {
      return 'text-primary-strong border-t-2 border-primary-strong bg-primary/10';
    }
    return 'text-ink-3 hover:text-ink-2 hover:bg-surface-sunken';
  };

  return (
    <nav
      className="fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200 z-50 safe-area-bottom"
      role="tablist"
      aria-label="Main navigation"
    >
      <div className="flex items-stretch h-16">
        {tabs.map((tab) => {
          const Icon = tab.icon;
          const isActive = activeTab === tab.id;

          return (
            <button
              key={tab.id}
              role="tab"
              aria-selected={isActive}
              aria-controls={`${tab.id}-panel`}
              onClick={() => setActiveTab(tab.id)}
              className={`
                flex-1 flex flex-col items-center justify-center gap-1
                transition-colors duration-150 min-h-[44px]
                ${getTabStyle(tab)}
              `}
            >
              <Icon
                size={22}
                strokeWidth={isActive ? 2.5 : 2}
                aria-hidden="true"
              />
              <span className="text-xs font-medium">
                {tab.label}
              </span>
            </button>
          );
        })}
      </div>
    </nav>
  );
}

export default MobileTabBar;
